import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { generateClient } from 'aws-amplify/api';
import * as queries from '../graphql/queries';
import jsPDF from 'jspdf';
import { getCustomerCheckInWithSignature } from '../graphql/customQueries';
import { fetchCustomer, saveCustomerTerms } from '../platform/engine/identitySpine';
import rekonLogo from '../Assets/Just Rekon.png';

const client = generateClient();

const companyInfo = {
  name: "Lucky's Truck & Trailer Sales",
  dba: "Dimmick Group Peterbilt",
  address: "65 Railroad Avenue",
  cityState: "Albany NY, 12205",
  phone: "PHONE (518) 453-1122",
  repairShop: "NYS Repair Shop # 7131447"
};

function CustomerDetailPage() {
  // The param is the customer's stable id (Phase 0.5); legacy bookmarks that
  // carry a raw company name keep working via the name fallback below.
  const { companyName } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [trucks, setTrucks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [termsSaving, setTermsSaving] = useState(false);
  const [termsSaved, setTermsSaved] = useState(false);

  useEffect(() => {
    fetchCustomerTrucks();
  }, [companyName]);

  const fetchCustomerTrucks = async () => {
    try {
      setIsLoading(true);

      // Try the param as a customerId first — the identity spine: matching
      // by id means a renamed customer keeps their whole history.
      const cust = await fetchCustomer(companyName);
      setCustomer(cust);

      const result = await client.graphql({
        query: queries.listTrucks,
        variables: {
          limit: 1000
        }
      });

      const customerTrucks = result.data.listTrucks.items.filter(truck =>
        cust
          ? (truck.customerId === cust.id || truck.companyName === cust.name)
          : truck.companyName === companyName
      );

      setTrucks(customerTrucks);
    } catch (error) {
      console.error('Error fetching customer trucks:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getCustomerSignature = async (customerCheckInId) => {
    if (!customerCheckInId) return null;

    try {
      console.log('Fetching signature for check-in ID:', customerCheckInId);

      const result = await client.graphql({
        query: getCustomerCheckInWithSignature,
        variables: { id: customerCheckInId }
      });

      return result.data.getCustomerCheckIn?.signatureImage || null;
    } catch (error) {
      console.error('Error fetching customer signature:', error);
      return null;
    }
  };

  const generateTruckPDF = async (truck) => {
    console.log('Generating PDF for truck:', truck.repairOrder);

    const doc = new jsPDF();

    try {
      doc.addImage(rekonLogo, 'PNG', 10, 10, 55, 18);

      doc.setFontSize(12);
      doc.text(companyInfo.name, 105, 12, { align: 'center' });
      doc.setFontSize(10);
      doc.text(companyInfo.dba, 105, 18, { align: 'center' });
      doc.text(companyInfo.address, 105, 24, { align: 'center' });
      doc.text(companyInfo.cityState, 105, 30, { align: 'center' });
      doc.text(companyInfo.phone, 105, 36, { align: 'center' });
      doc.setFontSize(8);
      doc.text(companyInfo.repairShop, 105, 42, { align: 'center' });
    } catch (error) {
      console.error('Error adding company header:', error);
      doc.setFontSize(12);
      doc.text(companyInfo.name, 105, 12, { align: 'center' });
      doc.setFontSize(10);
      doc.text(companyInfo.dba, 105, 18, { align: 'center' });
    }

    doc.setFontSize(20);
    doc.text(`${truck.repairOrder}`, 20, 55);

    doc.setFontSize(16);
    doc.text(`${new Date(truck.startTime).toLocaleDateString()}`, 20, 65);

    doc.setFontSize(12);
    let yPosition = 80;

    doc.text(`Company: ${truck.companyName}`, 20, yPosition);
    yPosition += 10;

    if (truck.contactPerson) {
      doc.text(`Contact: ${truck.contactPerson}`, 20, yPosition);
      yPosition += 10;
    }

    if (truck.phone) {
      doc.text(`Phone: ${truck.phone}`, 20, yPosition);
      yPosition += 10;
    }

    if (truck.email) {
      doc.text(`Email: ${truck.email}`, 20, yPosition);
      yPosition += 10;
    }

    yPosition += 5;
    doc.setFontSize(14);
    doc.text('Vehicle Information:', 20, yPosition);
    yPosition += 10;

    doc.setFontSize(12);
    if (truck.year || truck.make || truck.model) {
      doc.text(`Vehicle: ${truck.year || ''} ${truck.make || ''} ${truck.model || ''}`, 20, yPosition);
      yPosition += 10;
    }

    if (truck.vin) {
      doc.text(`VIN: ${truck.vin}`, 20, yPosition);
      yPosition += 10;
    }

    if (truck.mileage) {
      doc.text(`Mileage: ${truck.mileage}`, 20, yPosition);
      yPosition += 10;
    }

    if (truck.hours) {
      doc.text(`Engine Hours: ${truck.hours}`, 20, yPosition);
      yPosition += 10;
    }

    if (truck.faults) {
      doc.text(`Faults: ${truck.faults}`, 20, yPosition);
      yPosition += 10;
    }

    yPosition += 5;
    doc.setFontSize(14);
    doc.text('Service Information:', 20, yPosition);
    yPosition += 10;

    doc.setFontSize(12);
    doc.text(`Service Reason: ${truck.reason || ''}`, 20, yPosition);
    yPosition += 10;

    if (truck.partsHandling) {
      const partsText = truck.partsHandling === 'save' ? 'Save old parts' : 'Dispose of old parts';
      doc.text(`Parts Handling: ${partsText}`, 20, yPosition);
      yPosition += 10;
    }

    if (truck.warranty) {
      doc.text('Warranty Work: Yes', 20, yPosition);
      yPosition += 5;
    }

    yPosition += 1;
    doc.setFontSize(8);
    const disclaimerText = 'By signing this Repair Order, the customer authorizes Dimmick Group Peterbilt ("the Company") to perform any and all repair services needed and acknowledges that the Company is not responsible for loss of or damage to the vehicle or its contents due to fire, theft, vandalism, accident, or any cause beyond its control. All personal property and aftermarket accessories should be removed prior to drop-off, as the Company is not liable for any loss or damage to such items. The customer understands the vehicle may be road tested to verify repairs, and the Company is not responsible for damage caused by pre-existing conditions during such testing. The Company is not liable for delays due to parts availability, third-party services, or shipping issues. Vehicles left more than 48 hours after notification of repair completion may incur daily storage fees. The customer affirms they are authorized to approve repairs and acknowledges that any warranties are limited to those provided by the manufacturer or otherwise stated. Full payment is due upon completion of services, and the vehicle may be held until payment is received; additional storage or additional fees may apply.';

    const splitDisclaimer = doc.splitTextToSize(disclaimerText, 170);
    doc.text(splitDisclaimer, 20, yPosition);

    yPosition += (splitDisclaimer.length * 2.5) + 10;

    doc.setFontSize(12);
    doc.text('Customer Signature:', 20, yPosition);
    yPosition += 8;

    let signatureField = truck.signatureImage || truck.signature || truck.customerSignature || truck.signatureData;

    if (!signatureField && truck.customerCheckInId) {
      signatureField = await getCustomerSignature(truck.customerCheckInId);
    }

    if (signatureField) {
      try {
        if (signatureField.length < 100 || signatureField === 'AAAAAA==') {
          doc.setFontSize(10);
          doc.text('(Customer signature on file - data corrupted)', 20, yPosition);
          yPosition += 12;
        } else {
          const signatureData = signatureField.startsWith('data:image/')
            ? signatureField
            : `data:image/png;base64,${signatureField}`;

          doc.addImage(signatureData, 'PNG', 20, yPosition, 100, 30);
          yPosition += 35;
        }
      } catch (error) {
        console.error('Error adding signature to PDF:', error);
        doc.setFontSize(10);
        doc.text('(Customer signature on file)', 20, yPosition);
        yPosition += 12;
      }
    } else {
      doc.setFontSize(10);
      doc.text('(No signature available)', 20, yPosition);
      yPosition += 12;
    }

    doc.save(`${truck.companyName}_JOB_${truck.repairOrder}.pdf`);
  };

  // Net terms are this customer's, not the shop's. Estimates stamp the value at
  // draft time, so changing it here affects the NEXT estimate — invoices the
  // customer already holds keep the terms they were sent with.
  const saveBilling = async (patch) => {
    if (!customer?.id) return;
    setTermsSaving(true);
    const saved = await saveCustomerTerms(customer.id, patch);
    setTermsSaving(false);
    if (saved) {
      setCustomer(c => ({
        ...c,
        paymentTermsDays: saved.paymentTermsDays,
        poRequired: saved.poRequired,
      }));
      setTermsSaved(true);
      setTimeout(() => setTermsSaved(false), 2000);
    }
  };

  const filteredTrucks = trucks.filter(truck =>
    truck.repairOrder?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    truck.contactPerson?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    truck.vin?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    truck.make?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    truck.model?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="tt-page">
        <div className="tt-page-header">
          <div>
            <h1 className="tt-page-title">{customer?.name || decodeURIComponent(companyName)}</h1>
            <p className="tt-page-subtitle">Loading repairs...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="tt-page">
      <div className="tt-page-header">
        <div>
          <h1 className="tt-page-title">{customer?.name || decodeURIComponent(companyName)}</h1>
          <p className="tt-page-subtitle">Found {filteredTrucks.length} repair(s)</p>
        </div>
        <button className="tt-button tt-button--ghost tt-button--page" onClick={() => navigate('/customers')}>
          Back to Customers
        </button>
      </div>

      {customer?.id && (
        <div className="tt-card tt-list-card">
          <div className="tt-card-title">Billing terms</div>
          <div className="tt-terms-row">
            {[0, 15, 30, 45].map(days => {
              const active = (customer.paymentTermsDays ?? null) === days;
              return (
                <button
                  key={days}
                  className={`tt-button ${active ? 'tt-button--primary' : 'tt-button--ghost'}`}
                  disabled={termsSaving}
                  onClick={() => saveBilling({ paymentTermsDays: days })}
                >
                  {days === 0 ? 'COD' : `Net ${days}`}
                </button>
              );
            })}
            <span className="tt-terms-note">
              {customer.paymentTermsDays == null
                ? 'Using the shop default'
                : 'Applies to the next estimate — invoices already sent keep their terms'}
              {termsSaved ? ' · Saved' : ''}
            </span>
          </div>

          {/* Fleets and municipalities return an invoice that has no PO on it.
              This only warns at invoice time — it never blocks the invoice. */}
          <label className="tt-checkbox-row" style={{ marginTop: 12 }}>
            <input
              type="checkbox"
              checked={!!customer.poRequired}
              disabled={termsSaving}
              onChange={e => saveBilling({ poRequired: e.target.checked })}
            />
            <span>Requires a PO number on every invoice</span>
          </label>
        </div>
      )}

      <div className="tt-toolbar">
        <input
          className="tt-input"
          type="text"
          placeholder="Search by Job #, contact, VIN, make, model..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ maxWidth: '420px' }}
        />
      </div>

      {filteredTrucks.length === 0 ? (
        <div className="tt-card tt-list-card">
          <p className="tt-empty">No repairs found for this customer.</p>
        </div>
      ) : (
        <div>
          {filteredTrucks.map((truck) => (
            <div key={truck.id} className="tt-card tt-list-card">
              <div className="tt-card-actions" style={{ marginTop: 0, alignItems: 'flex-start' }}>
                <div className="tt-customer-details" style={{ marginTop: 0 }}>
                  <div className="tt-card-title">Job #{truck.repairOrder}</div>
                  {truck.contactPerson && <div><strong>Contact:</strong> {truck.contactPerson}</div>}
                  {truck.phone && <div><strong>Phone:</strong> {truck.phone}</div>}
                  {truck.email && <div><strong>Email:</strong> {truck.email}</div>}
                  <div><strong>Vehicle:</strong> {truck.year || ''} {truck.make || ''} {truck.model || ''}</div>
                  {truck.vin && <div><strong>VIN:</strong> {truck.vin}</div>}
                  {truck.mileage && <div><strong>Mileage:</strong> {truck.mileage}</div>}
                  {truck.hours && <div><strong>Hours:</strong> {truck.hours}</div>}
                  {truck.faults && <div><strong>Faults:</strong> {truck.faults}</div>}
                  <div><strong>Service Reason:</strong> {truck.reason}</div>
                  <div><strong>Status:</strong> {truck.status}</div>
                  <div><strong>Date:</strong> {new Date(truck.startTime).toLocaleDateString()}</div>
                </div>

                <button className="tt-button tt-button--primary" onClick={async () => await generateTruckPDF(truck)}>
                  Generate PDF
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default CustomerDetailPage;
